<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import validations, {
  getLabelTitleErrorMessage,
} from 'dashboard/routes/dashboard/settings/labels/validations';
// SUMO (sovpalo) brand palette — this dialog renders inside the Chatwoot iframe,
// so the colors/borders are applied inline rather than via design tokens
// (and to override Chatwoot's global form styles).
const SUMO = {
  navy: '#0e243b',
  border: '#dee2e6',
  divider: '#e1e3eb',
  primary: '#232ED1',
  defaultColor: '#7A5AFF',
  error: '#fe4a49',
};

export default {
  props: {
    // When present the dialog edits an existing label, otherwise it creates one.
    label: {
      type: Object,
      default: null,
    },
  },
  emits: ['close'],
  setup() {
    return { v$: useVuelidate(), SUMO };
  },
  data() {
    return {
      color: SUMO.defaultColor,
      description: '',
      title: '',
      showOnSidebar: false,
      labelType: 'private',
    };
  },
  validations,
  computed: {
    ...mapGetters({
      uiFlags: 'labels/getUIFlags',
    }),
    isEdit() {
      return Boolean(this.label?.id);
    },
    isSaving() {
      return this.uiFlags.isCreating || this.uiFlags.isUpdating;
    },
    labelTitleErrorMessage() {
      return this.$t(getLabelTitleErrorMessage(this.v$));
    },
    fieldStyle() {
      return {
        color: SUMO.navy,
        backgroundColor: '#fff',
      };
    },
    nameStyle() {
      return {
        color: SUMO.navy,
        backgroundColor: '#fff',
      };
    },
    selectStyle() {
      return {
        ...this.fieldStyle,
        appearance: 'none',
        webkitAppearance: 'none',
        mozAppearance: 'none',
        backgroundImage: 'none',
      };
    },
  },
  mounted() {
    if (this.isEdit) {
      this.title = this.label.title;
      this.description = this.label.description || '';
      this.color = this.label.color;
      this.showOnSidebar = this.label.show_on_sidebar;
      this.labelType = this.label.label_type || 'public';
    }
  },
  methods: {
    onClose() {
      this.$emit('close');
    },
    async onSubmit() {
      this.v$.$touch();
      if (this.v$.title.$invalid || this.v$.color.$invalid) {
        return;
      }
      try {
        if (this.isEdit) {
          // label_type is immutable on update (backend ignores it).
          await this.$store.dispatch('labels/update', {
            id: this.label.id,
            color: this.color,
            description: this.description,
            title: this.title.toLowerCase(),
            show_on_sidebar: this.showOnSidebar,
          });
          useAlert(this.$t('LABEL_MGMT.EDIT.API.SUCCESS_MESSAGE'));
        } else {
          await this.$store.dispatch('labels/create', {
            color: this.color,
            description: this.description,
            title: this.title.toLowerCase(),
            show_on_sidebar: this.showOnSidebar,
            label_type: this.labelType,
          });
          useAlert(this.$t('LABEL_MGMT.ADD.API.SUCCESS_MESSAGE'));
        }
        this.onClose();
      } catch (error) {
        const fallback = this.isEdit
          ? this.$t('LABEL_MGMT.EDIT.API.ERROR_MESSAGE')
          : this.$t('LABEL_MGMT.ADD.API.ERROR_MESSAGE');
        useAlert(error.message || fallback);
      }
    },
  },
};
</script>

<template>
  <div class="flex flex-col w-full bg-white rounded-xl">
    <!-- Header -->
    <div class="px-6 py-3 border-b" :style="{ borderColor: SUMO.divider }">
      <h3 class="text-xl font-bold" :style="{ color: SUMO.navy }">
        {{
          isEdit
            ? $t('LABEL_MGMT.OMNI_POPUP.EDIT_TITLE')
            : $t('LABEL_MGMT.OMNI_POPUP.CREATE_TITLE')
        }}
      </h3>
    </div>

    <!-- Body -->
    <div class="flex flex-col gap-2.5 px-6 py-4">
      <!-- Type -->
      <div class="flex flex-col gap-1.5">
        <label class="text-sm font-medium" :style="{ color: SUMO.navy }">
          {{ $t('LABEL_MGMT.FORM.TYPE.LABEL') }}
        </label>
        <div class="relative">
          <select
            v-model="labelType"
            :disabled="isEdit"
            class="cw-field w-full h-10 px-3 mb-0 text-sm leading-normal rounded-lg appearance-none cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            :style="selectStyle"
          >
            <option value="private">
              {{ $t('LABEL_MGMT.FORM.TYPE.PERSONAL') }}
            </option>
            <option value="public">
              {{ $t('LABEL_MGMT.FORM.TYPE.WORKSPACE') }}
            </option>
          </select>
          <span
            class="absolute -translate-y-1/2 pointer-events-none i-lucide-chevron-down size-4 top-1/2 ltr:right-3 rtl:left-3"
            :style="{ color: SUMO.navy }"
          />
        </div>
      </div>

      <!-- Name + Color -->
      <div class="flex items-start gap-3">
        <div class="flex flex-col flex-1 gap-1.5">
          <label class="text-sm font-medium" :style="{ color: SUMO.navy }">
            {{ $t('LABEL_MGMT.OMNI_POPUP.NAME') }}
          </label>
          <input
            v-model="title"
            type="text"
            class="cw-field w-full h-10 px-3 mb-0 text-sm rounded-lg outline-none label-name--input"
            :class="{ 'has-error': v$.title.$error }"
            :style="nameStyle"
            @input="v$.title.$touch"
            @blur="v$.title.$touch"
            @keyup.enter="onSubmit"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium" :style="{ color: SUMO.navy }">
            {{ $t('LABEL_MGMT.FORM.COLOR.LABEL') }}
          </label>
          <div
            class="flex items-center justify-center h-10 gap-1 px-2 rounded-lg color-box"
            :style="fieldStyle"
          >
            <woot-color-picker v-model="color" />
            <span
              class="i-lucide-chevron-down size-4"
              :style="{ color: SUMO.navy }"
            />
          </div>
        </div>
      </div>
      <p
        v-if="v$.title.$error"
        class="-mt-1 text-xs"
        :style="{ color: SUMO.error }"
      >
        {{ labelTitleErrorMessage }}
      </p>

      <!-- Description -->
      <div class="flex flex-col gap-1.5">
        <label class="text-sm font-medium" :style="{ color: SUMO.navy }">
          {{ $t('LABEL_MGMT.FORM.DESCRIPTION.LABEL') }}
        </label>
        <textarea
          v-model="description"
          rows="3"
          class="cw-field w-full px-3 py-2 mb-0 text-sm outline-none resize-none rounded-lg"
          :style="fieldStyle"
        />
      </div>

      <!-- Show on sidebar -->
      <label class="flex items-center gap-2 cursor-pointer">
        <input
          v-model="showOnSidebar"
          type="checkbox"
          class="rounded cursor-pointer size-4"
          :style="{ accentColor: SUMO.primary }"
        />
        <span class="text-sm font-medium" :style="{ color: SUMO.navy }">
          {{ $t('LABEL_MGMT.FORM.SHOW_ON_SIDEBAR.LABEL') }}
        </span>
      </label>
    </div>

    <!-- Footer -->
    <div
      class="flex items-center gap-4 px-6 py-3 border-t"
      :style="{ borderColor: SUMO.divider }"
    >
      <button
        type="button"
        class="px-6 py-2 text-sm font-semibold text-white rounded-lg disabled:opacity-50"
        :style="{ backgroundColor: SUMO.primary }"
        :disabled="isSaving"
        @click="onSubmit"
      >
        {{ $t('LABEL_MGMT.FORM.SAVE') }}
      </button>
      <button
        type="button"
        class="text-sm font-semibold bg-transparent"
        :style="{ color: SUMO.primary }"
        @click="onClose"
      >
        {{ $t('LABEL_MGMT.FORM.CANCEL') }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// Label API supports only lowercase letters
.label-name--input {
  @apply lowercase;
}

// Force a single, crisp border over Chatwoot's global input styles
// (which add a box-shadow ring that looked like a double border).
.cw-field {
  border: 1px solid #dee2e6 !important;
  box-shadow: none !important;
  background-color: #fff !important;
}
.cw-field.has-error {
  border-color: #fe4a49 !important;
}

// The color box is a div (not a form control), so it keeps its own border.
.color-box {
  border: 1px solid #dee2e6;
}

// Center the color swatch inside the color box (the picker adds a bottom margin).
.color-box :deep(.colorpicker--selected) {
  @apply mb-0 size-6;
}

// Open the color picker leftwards so it isn't clipped by the dialog's right edge.
.color-box :deep(.colorpicker--chrome) {
  left: auto;
  right: 0;
}
</style>
